import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload as AntUpload } from 'antd';
import { HttpRequestHeader, RcFile, UploadFile } from 'antd/lib/upload/interface';
import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import { useApiFetch } from '../../hooks/api/useApiFetch';
import { IUploadLayout } from '../../types/layouts/upload';
import { IFormItemProps } from '../../factories/form';

interface IFile {
    id: string;
    url: string;
    original_name: string;
}

function Upload({
    accept, additionalBody, formKey, listType, uploadAction, listApi, deleteAction, hidden, multiple, onChange, disabled,
}: IUploadLayout['config'] & IFormItemProps) {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [shouldUpload, setShouldUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const filesToUpload = useRef<RcFile[]>([]);

    const { getHeaders, fetchData } = useApiFetch();

    const headers = getHeaders(true) as HttpRequestHeader;

    const beforeUpload = (file: RcFile) => {
        filesToUpload.current.push(file);
        setShouldUpload(true);
        return false;
    };

    const getFileList = async () => {
        if (!hidden && typeof listApi === 'string') {
            const result = await fetchData<string|IFile[]>(listApi, 'get', true);
            const value = result.data;

            if (result.success) {
                if (typeof value === 'string') {
                    setFileList([{
                        name: 'image',
                        uid: '1',
                        url: value,
                    }]);
                }

                if (Array.isArray(value)) {
                    setFileList(value.map((val) => ({
                        name: val.original_name,
                        uid: val.id,
                        url: val.url,
                    })));
                }
            } else {
                setFileList([]);
            }
        }
    };

    const uploadFile = async () => {
        if (shouldUpload && filesToUpload.current.length > 0) {
            if (typeof uploadAction === 'string') {
                setUploading(true);

                const formData = new FormData();

                filesToUpload.current.forEach((file) => {
                    formData.append(formKey, file);
                });

                if (additionalBody && typeof additionalBody === 'object') {
                    const keys = Object.keys(additionalBody);
                    keys.forEach((key) => {
                        formData.append(key, additionalBody[key]);
                    });
                }

                const result = await fetchData(uploadAction, 'post', true, formData);

                if (result.success) {
                    getFileList();
                }

                setUploading(false);
            } else {
                setFileList(produce(fileList, (draft) => {
                    draft.push(...filesToUpload.current);
                }));
            }
            filesToUpload.current = [];
            setShouldUpload(false);
        }
    };

    const onRemove = async (file: UploadFile) => {
        if (typeof deleteAction === 'string') {
            setUploading(true);
            await fetchData(deleteAction.replace('{id}', file.uid), 'get', true);
            getFileList();
            setUploading(false);
        } else {
            const nextState = produce(fileList, (draft) => {
                const index = draft.findIndex((el) => el.uid === file.uid);
                draft.splice(index, 1);
            });
            setFileList(nextState);
            if (typeof onChange === 'function') {
                onChange(nextState);
            }
        }
    };

    useEffect(() => {
        getFileList();
    }, []);

    useEffect(() => {
        uploadFile();
    }, [shouldUpload]);

    useEffect(() => {
        if (typeof uploadAction !== 'string' && typeof onChange === 'function') {
            onChange(fileList);
        }
    }, [fileList]);

    return (
        <AntUpload
            className={disabled ? 'ant-upload-disabled' : undefined}
            accept={accept}
            fileList={fileList}
            headers={headers}
            listType={listType}
            name={formKey}
            beforeUpload={beforeUpload}
            onRemove={onRemove}
            multiple={typeof multiple === 'boolean' ? multiple : true}
        >
            <Button icon={<UploadOutlined />} loading={uploading}>Загрузить</Button>
        </AntUpload>
    );
}

export default Upload;
